// deployment_class: customer_env_allowed
/**
 * CEO redesign — the project VISUAL layer (live, in-app · N76 single artifact).
 *
 *   Cover       — Kathedral's OWN cover treatment: a tinted gradient + a clean
 *                 architectural skyline. Not a stock photo, not a copy of any
 *                 site; swap-ready (a real `Deal.img` URL overrides it).
 *   ProjectGrid — the Projects overview as a visual photo-card grid; each card
 *                 opens the project's pic view (which carries the Data Ticker).
 */
import { useState, type CSSProperties, type KeyboardEvent } from 'react';
import { type Deal } from './project-data';
import { useDeals } from './dashboard-source';
import { Analyzer } from './AnalyzerView';

/** The owned cover visual — a clean branded gradient (swap-ready for a real photo). */
export function Cover({ d, big }: { d: Deal; big?: boolean }): JSX.Element {
  const style = { ['--h' as string]: String(d.tint) } as CSSProperties;
  const photo = d.img ? { backgroundImage: `url(${d.img})` } : undefined;
  return (
    <div className={`kd-cover ${big ? 'big' : ''} ${d.img ? 'photo' : ''}`} style={{ ...style, ...photo }} aria-hidden="true">
      {/* The type tag is redundant on the hero (the eyebrow already reads
          "Project · <type>") — removed there per bug_UI_1c. Kept on the small
          pipeline thumbnails where it labels the grid. */}
      {!big && <span className="kd-covtag">{d.type}</span>}
    </div>
  );
}

function activate(fn: () => void) {
  return (e: KeyboardEvent): void => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); } };
}

type PortfolioView = 'grid' | 'analyze';

/** The Projects overview — a visual GRID of project photo-cards, or the Analyze
 *  compare view. Toggle persists per browser. ("Free" board removed 2026-07-14
 *  per E3 — it didn't earn its place.) */
export function ProjectGrid({ onOpen, isPinned, onTogglePin }: {
  onOpen: (k: string) => void;
  isPinned: (projectKey: string) => boolean;
  onTogglePin: (projectKey: string, label: string) => void;
}): JSX.Element {
  const { deals: DEALS, order: DEAL_ORDER } = useDeals();
  const [view, setView] = useState<PortfolioView>(() => {
    try { const v = localStorage.getItem('kd-portfolio-view'); return v === 'analyze' ? v : 'grid'; } catch { return 'grid'; }
  });
  const pick = (v: PortfolioView): void => {
    setView(v);
    try { localStorage.setItem('kd-portfolio-view', v); } catch { /* non-persistent env */ }
  };

  const toggle = (
    <div className="kd-pviews" role="group" aria-label="Portfolio view">
      <button className={`kd-pview ${view === 'grid' ? 'on' : ''}`} aria-pressed={view === 'grid'} onClick={() => pick('grid')}>▦ Grid</button>
      <button className={`kd-pview ${view === 'analyze' ? 'on' : ''}`} aria-pressed={view === 'analyze'} onClick={() => pick('analyze')}>⚖ Analyze</button>
    </div>
  );

  if (view === 'analyze') {
    return (<>{toggle}<Analyzer onOpen={onOpen} /></>);
  }

  return (
    <>
      {toggle}
      <div className="kd-pgrid">
        {DEAL_ORDER.map((k) => {
          const d = DEALS[k];
          const pinned = isPinned(k);
          return (
            <div className="kd-pcard" key={k} role="button" tabIndex={0}
              onClick={() => onOpen(k)} onKeyDown={activate(() => onOpen(k))} aria-label={`Open ${d.name}`}>
              <Cover d={d} />
              <button className={`kd-ppin ${pinned ? 'on' : ''}`} aria-pressed={pinned}
                aria-label={`${pinned ? 'Unpin' : 'Pin'} ${d.name}`}
                onClick={(e) => { e.stopPropagation(); onTogglePin(k, d.name); }}>📌</button>
              <div className="kd-pcbody">
                <div className="kd-pctop">
                  <div className="kd-pcname">{d.name}<span className="kd-pctype">{d.type}</span></div>
                  <span className={`kd-rag ${d.rag[0]}`}>{d.rag[1]}</span>
                </div>
                <div className="kd-pcstats">
                  <span><b>{d.units}</b> units</span>
                  <span><b>{d.budget}</b> TDC</span>
                  <span>DSCR <b>{d.dscr}</b></span>
                  <span className="kd-pcdev">{d.dev}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
